import React, { useEffect, useState } from "react";
import { Copy, KeyRound, Check, X } from "lucide-react";

/**
 * Depois de gerar o código de 1º acesso: janela com a mensagem pronta, botão "Copiar" e "Enviar no WhatsApp".
 * (No celular o navegador não deixa copiar sozinho depois de esperar o servidor: a cópia precisa de um toque.)
 * Uso: mostrarAcesso(resposta_do_resend_invite)   — o <AccessShareHost /> fica montado uma vez no App.
 */
let abrir = null;
export function mostrarAcesso(dados) {
  if (abrir) abrir(dados);
}

const WhatsIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.27.49 1.7.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.18-1.41-.07-.13-.27-.2-.57-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88a9.83 9.83 0 0 1 6.99 2.9 9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.89 9.88m8.41-18.29A11.81 11.81 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.89 0-3.18-1.24-6.16-3.48-8.41" />
  </svg>
);

// celular brasileiro para o link do WhatsApp (55 + DDD + número)
function foneWa(f) {
  let d = String(f || "").replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.length === 10 || d.length === 11) d = "55" + d;
  return d.length >= 12 ? d : "";
}

export function AccessShareHost() {
  const [d, setD] = useState(null);
  const [copiado, setCopiado] = useState(false);
  useEffect(() => { abrir = (x) => { setCopiado(false); setD(x); }; return () => { abrir = null; }; }, []);
  if (!d) return null;
  const msg = d.message || d.invite_link || "";
  const wa = foneWa(d.phone);
  const copiar = async () => {
    try { await navigator.clipboard.writeText(msg); setCopiado(true); }
    catch (e) {   // navegador sem permissão: seleciona o texto para copiar à mão
      const el = document.getElementById("acesso-msg"); el?.focus(); el?.select();
    }
  };
  return (
    <div className="fixed inset-0 z-[190] grid place-items-center p-4 bg-[#0F1730]/55 backdrop-blur-[2px]" style={{ pointerEvents: "auto" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) setD(null); }} data-testid="access-share">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        <div className="h-1.5" style={{ background: "linear-gradient(90deg,#232D4F,#1E9E2A)" }} />
        <div className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 shrink-0 rounded-full grid place-items-center bg-green-50 text-[#1E9E2A]"><KeyRound className="h-5 w-5" /></div>
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-lg font-bold text-slate-900">Novo código de acesso</h2>
              <p className="text-sm text-slate-600">{d.name ? <strong>{d.name}</strong> : "Aluna"} · código <span className="font-mono font-bold text-[#232D4F]">{d.code}</span></p>
              <p className="text-xs text-slate-500 mt-0.5">{d.email_sent ? "O e-mail com o código já foi enviado." : "O e-mail não foi enviado: mande a mensagem abaixo pelo WhatsApp."}</p>
            </div>
            <button onClick={() => setD(null)} className="p-1 rounded text-slate-400 hover:bg-slate-100" aria-label="Fechar"><X className="h-4 w-4" /></button>
          </div>
          <textarea id="acesso-msg" readOnly value={msg} rows={6} className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 resize-none" />
          <div className="grid gap-2 sm:grid-cols-2">
            <button onClick={copiar} data-testid="access-copy"
              className="h-11 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center justify-center gap-2">
              {copiado ? <><Check className="h-4 w-4 text-[#1E9E2A]" /> Copiado!</> : <><Copy className="h-4 w-4" /> Copiar mensagem</>}
            </button>
            <a href={wa ? `https://wa.me/${wa}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noopener noreferrer" data-testid="access-whatsapp"
              className="h-11 rounded-lg bg-[#1E9E2A] hover:bg-green-700 text-sm font-semibold text-white inline-flex items-center justify-center gap-2">
              <WhatsIcon className="h-4 w-4" /> {wa ? "Enviar no WhatsApp dela" : "Enviar pelo WhatsApp"}
            </a>
          </div>
          {!wa && <p className="text-[11px] text-slate-400">Sem celular no cadastro: o WhatsApp abre para você escolher o contato.</p>}
        </div>
      </div>
    </div>
  );
}
